/**
 * Card-first trial gate (PANDA-2341).
 *
 * The product decision: a signed-in user cannot do meaningful Panda work until
 * a payment method is on file. They type their idea, sign up, come back to the
 * builder with the prompt intact, press Build — and THAT is where the trial
 * modal appears. Not at sign-in (a card ask against a blank screen), and not at
 * credit exhaustion (which is where today's trial offer lives).
 *
 * WHY THIS IS A SERVER DECISION, AND WHY IT IS ONE FUNCTION.
 *
 * The whole point of the gate is that it cannot be talked around. A client that
 * decides "this user looks free, show the card modal" is a client that can be
 * wrong in both directions: it blocks a paying customer whose plan hasn't
 * loaded, and it lets a build start for someone with no card because a fetch
 * was slow. So the decision is taken once, on the server, from typed billing
 * state, and delivered as a 402 `reason` on the pre-flight the build already
 * calls (`POST /api/credits/check`). The client renders that reason; it never
 * derives it.
 *
 * This module is the whole rule. It is pure and client-safe so the route, the
 * tests and any future surface all read the SAME predicate rather than
 * re-deriving "is this account gated" from scattered plan-string comparisons —
 * the drift that lib/plans.ts#isPaidPlan exists to prevent for its own question.
 */

import { isPaidPlan, type PlanId } from "@/lib/plans";
import { TRIAL_CREDITS, TRIAL_DAYS, trialCopy } from "@/lib/trial/config";

/**
 * Actions the gate applies to.
 *
 * These are the NORMALIZED buckets `/api/credits/check` already reduces every
 * client label to (`startActionFor`), not the raw strings the canvas sends.
 * Gating on the normalized value is what stops the wall drifting: a new client
 * label added tomorrow still lands in one of these three buckets, so it is
 * gated by construction rather than by someone remembering to extend a list.
 *
 * All three spend credits and run the model. The ticket's "read-only browsing
 * remains available" is about NAVIGATION — opening projects, account, billing —
 * not about asking the model things for free, so `question` is gated too.
 */
const GATED_ACTIONS: ReadonlySet<string> = new Set([
  "build",
  "edit",
  "question",
]);

export function isGatedAction(action: string | null | undefined): boolean {
  return typeof action === "string" && GATED_ACTIONS.has(action);
}

export interface CardFirstGateInput {
  /** Canonical plan from readEntitlementState — never the user_credits mirror. */
  plan: PlanId | string | null | undefined;
  /** user_billing.trial_status: 'active' | 'converted' | 'cancelled' | 'expired' | null. */
  trialStatus: string | null | undefined;
  /** user_billing.trial_used — the lifetime one-trial-per-account latch. */
  trialUsed: boolean | null | undefined;
  /** A payment method is on file with any provider. */
  hasCard: boolean;
  /** Normalized action bucket from `startActionFor` — "build" | "edit" | "question". */
  action: string | null | undefined;
}

export type CardFirstGateReason =
  /** Gate applies: no card, no trial history, free plan. */
  | "card_required"
  /** Not gated, and why — kept typed so the route can log a decision trail. */
  | "paid_plan"
  | "trial_active"
  | "trial_used"
  | "has_card"
  | "ungated_action"
  | "disabled";

export interface CardFirstGateDecision {
  gated: boolean;
  reason: CardFirstGateReason;
}

/**
 * Is the card-first gate switched on for this deployment?
 *
 * Read at CALL time, not module-eval time, for the same reason
 * lib/trial/config.ts#holdoutPercent is: this is a rollout switch, and freezing
 * it at import makes it impossible to exercise both sides in a test without
 * module-registry surgery.
 *
 * Defaults OFF. A wall that appears because someone forgot to set a variable is
 * an outage, not a launch.
 */
export function isCardFirstGateEnabled(): boolean {
  return process.env.NEXT_PUBLIC_CARD_FIRST_TRIAL === "1";
}

/**
 * The rule, in precedence order. Order matters only for which `reason` is
 * reported — any single passing check means the user is NOT gated.
 *
 *   paid            — they are already a customer.
 *   trial active    — they are mid-trial; the trial's own surfaces own them.
 *   trial used      — they have had their one trial. Running out now is the
 *                     PAYWALL's job, not this gate's: re-showing a "start your
 *                     free trial" modal to someone who already spent theirs is
 *                     the bait the eligibility check in lib/trial/eligibility.ts
 *                     exists to prevent, and it would 403 at /trial/start anyway.
 *   has card        — the wall has already been cleared. A card on file with no
 *                     trial and no plan is an odd state (an abandoned checkout,
 *                     an admin top-up), but the ASK has been satisfied and
 *                     asking again would be a wall with nothing behind it.
 *   ungated action  — browsing, settings, publish checks.
 */
export function evaluateCardFirstGate(
  input: CardFirstGateInput,
): CardFirstGateDecision {
  if (!isCardFirstGateEnabled()) return { gated: false, reason: "disabled" };
  if (!isGatedAction(input.action)) return { gated: false, reason: "ungated_action" };
  if (isPaidPlan(input.plan)) return { gated: false, reason: "paid_plan" };
  if (input.trialStatus === "active") return { gated: false, reason: "trial_active" };
  if (input.trialUsed === true) return { gated: false, reason: "trial_used" };
  if (input.hasCard) return { gated: false, reason: "has_card" };
  return { gated: true, reason: "card_required" };
}

/**
 * The 402 body the pre-flight returns when the gate fires.
 *
 * Shaped like the existing credit 402s (`reason` + `credits_remaining`) so the
 * canvas's single 402 handler routes it with no new transport, and carries the
 * offer's own figures so the modal never hardcodes a price or an allowance.
 */
export interface CardRequiredPayload {
  error: "credit_limit_reached";
  reason: "card_required";
  credits_remaining: number;
  trial: {
    days: number;
    credits: number;
    priceLabel: string;
    convertPriceLabel: string;
    termsLine: string;
  };
}

/**
 * The offer's own figures, as the wall renders them. One builder, so the 402
 * body and every balance surface that opens the wall quote the same terms.
 */
export function cardFirstTrialTerms(): CardRequiredPayload["trial"] {
  return {
    days: TRIAL_DAYS,
    credits: TRIAL_CREDITS,
    priceLabel: trialCopy.priceLabel,
    convertPriceLabel: trialCopy.convertPriceLabel,
    termsLine: trialCopy.termsLine,
  };
}
